/**
 * Dedicated cart-drawer AJAX upsell loader.
 * Kept separate from product-recommendations so header hydration morphs cannot wipe it.
 */
class CartDrawerUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {number | null} */
  #reloadTimer = null;

  /** @type {AbortController | null} */
  #eventsAbort = null;

  connectedCallback() {
    this.#eventsAbort = new AbortController();
    const { signal } = this.#eventsAbort;

    document.addEventListener('cart:update', this.#scheduleReload, { signal });
    this.addEventListener('cart-drawer-upsell:load', () => this.#load(), { signal });

    // Always fetch on connect — drawer content is in DOM even when closed
    this.#load();
  }

  disconnectedCallback() {
    this.#eventsAbort?.abort();
    this.#activeFetch?.abort();
    if (this.#reloadTimer) window.clearTimeout(this.#reloadTimer);
  }

  #scheduleReload = () => {
    if (this.#reloadTimer) window.clearTimeout(this.#reloadTimer);
    this.#reloadTimer = window.setTimeout(() => {
      this.dataset.loaded = 'false';
      this.#load();
    }, 150);
  };

  async #load() {
    const productId = this.#resolveProductId();
    const sectionId = this.dataset.sectionId || 'cart-drawer-upsell';
    const intent = this.dataset.intent || 'related';
    const fallbackIntent = this.dataset.fallbackIntent || 'complementary';
    const baseUrl = this.dataset.url || '/recommendations/products?limit=4';

    if (!productId) {
      this.#renderEmpty();
      return;
    }

    this.hidden = false;
    this.setAttribute('aria-busy', 'true');

    try {
      let html = await this.#fetchSection(baseUrl, productId, sectionId, intent);
      if (!this.#hasItems(html) && fallbackIntent && fallbackIntent !== intent) {
        html = await this.#fetchSection(baseUrl, productId, sectionId, fallbackIntent);
      }
      this.#applyMarkup(html);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Cart drawer upsell error:', error);
      this.#renderEmpty();
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @returns {string}
   */
  #resolveProductId() {
    if (this.dataset.productId) return this.dataset.productId;

    const drawer = this.closest('cart-drawer-component');
    const fromCartLine = drawer?.querySelector('[data-product-id]');
    if (fromCartLine instanceof HTMLElement && fromCartLine.dataset.productId) {
      return fromCartLine.dataset.productId;
    }

    return '';
  }

  /**
   * @param {string} baseUrl
   * @param {string} productId
   * @param {string} sectionId
   * @param {string} intent
   */
  async #fetchSection(baseUrl, productId, sectionId, intent) {
    const url = `${baseUrl}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const response = await fetch(url, { signal: this.#activeFetch.signal });
    if (!response.ok) throw new Error(`Upsell request failed (${response.status})`);
    return response.text();
  }

  /**
   * @param {string} html
   */
  #hasItems(html) {
    return html.includes('cart-drawer__upsell-item');
  }

  /**
   * @param {string} html
   */
  #applyMarkup(html) {
    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const source =
      fragment.querySelector('cart-drawer-upsell') ||
      fragment.querySelector('product-recommendations[data-cart-upsell="true"]') ||
      fragment.querySelector('[data-hydration-key="cart-drawer-upsell"]');

    if (!source) {
      this.#renderEmpty();
      return;
    }

    const items = source.querySelectorAll('.cart-drawer__upsell-item');
    if (!items.length) {
      this.#renderEmpty();
      return;
    }

    this.innerHTML = source.innerHTML;
    this.hidden = false;
    this.dataset.loaded = 'true';
    this.classList.add('cart-drawer__upsell--loaded');
  }

  #renderEmpty() {
    this.innerHTML = '';
    this.hidden = true;
    this.dataset.loaded = 'false';
    this.classList.remove('cart-drawer__upsell--loaded');
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
